import { useState } from "react";
import api from "../../services/api";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import "./ProfitReport.css";

export default function ProfitReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  // 🔹 Generar reporte
  const handleGenerateReport = async () => {
    if (!fromDate || !toDate) {
      return Swal.fire("Atención", "Debes seleccionar ambas fechas", "warning");
    }

    if (new Date(fromDate) > new Date(toDate)) {
      return Swal.fire("Error", "La fecha inicial no puede ser mayor a la fecha final", "error");
    }

    setLoading(true);
    try {
      const response = await api.get("/reports/profit", {
        params: { from: fromDate, to: toDate }
      });

      setReportData(response.data);
      
      Swal.fire({
        icon: "success",
        title: "Reporte Generado",
        text: "El reporte de ganancias se generó correctamente",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Error generando reporte:", error);
      Swal.fire("Error", "No se pudo generar el reporte de ganancias", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Limpiar reporte
  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setReportData(null);
  };

  // 🔹 Exportar a Excel - VERSIÓN ACTUALIZADA CON 3 HOJAS
  const handleExportExcel = () => {
    if (!reportData) return;

    const workbook = XLSX.utils.book_new();

    // ==========================================
    // HOJA 1: GANANCIAS POR DÍA
    // ==========================================
    
    const dataPorDia = [
      ["GANANCIAS POR DÍA"],
      ["Período:", `${reportData.periodo.desde} al ${reportData.periodo.hasta}`],
      [],
      ["FECHA", "TOTAL VENDIDO", "COSTO TOTAL", "GANANCIA", "MARGEN %"],
    ];
    
    // Agregar datos por día (viene del backend)
    if (reportData.por_dia && reportData.por_dia.length > 0) {
      reportData.por_dia.forEach(dia => {
        dataPorDia.push([
          dia.fecha,
          `$${dia.total_vendido.toFixed(2)}`,
          `$${dia.costo_total.toFixed(2)}`,
          `$${dia.ganancia.toFixed(2)}`,
          `${dia.margen_porcentaje.toFixed(2)}%`
        ]);
      });
      
      // Agregar totales
      dataPorDia.push([]);
      dataPorDia.push([
        "TOTALES",
        `$${reportData.resumen.total_vendido.toFixed(2)}`,
        `$${reportData.resumen.costo_total.toFixed(2)}`,
        `$${reportData.resumen.ganancia_total.toFixed(2)}`,
        `${reportData.resumen.margen_promedio.toFixed(2)}%`
      ]);
    } else {
      // Si no hay datos por día, mostrar solo resumen
      dataPorDia.push([
        "TODO EL PERÍODO",
        `$${reportData.resumen.total_vendido.toFixed(2)}`,
        `$${reportData.resumen.costo_total.toFixed(2)}`,
        `$${reportData.resumen.ganancia_total.toFixed(2)}`,
        `${reportData.resumen.margen_promedio.toFixed(2)}%`
      ]);
    }
    
    const wsPorDia = XLSX.utils.aoa_to_sheet(dataPorDia);
    XLSX.utils.book_append_sheet(workbook, wsPorDia, "Ganancias por Día");

    // ==========================================
    // HOJA 2: GANANCIAS POR PRODUCTO
    // ==========================================
    
    const dataPorProducto = [
      ["GANANCIAS POR PRODUCTO"],
      ["Período:", `${reportData.periodo.desde} al ${reportData.periodo.hasta}`],
      [],
      ["FECHA", "CÓDIGO", "NOMBRE", "PRESENTACIÓN", "TIPO", "CANT.", "UNID. BASE", "P. COMPRA", "P. VENTA", "COSTO TOTAL", "VENTA TOTAL", "GANANCIA", "MARGEN %"],
    ];
    
    // Agregar cada producto
    reportData.detalle.forEach(item => {
      dataPorProducto.push([
        item.fecha || "-",
        item.codigo,
        item.nombre,
        item.presentacion,
        item.tipo,
        item.cantidad_vendida,
        item.unidades_base,
        item.precio_compra_unitario ? `$${item.precio_compra_unitario.toFixed(2)}` : "-",
        `$${item.precio_venta.toFixed(2)}`,
        `$${item.costo_total.toFixed(2)}`,
        `$${item.venta_total.toFixed(2)}`,
        `$${item.ganancia_neta.toFixed(2)}`,
        `${item.margen_porcentaje.toFixed(2)}%`
      ]);
    });
    
    // Agregar totales
    dataPorProducto.push([]);
    dataPorProducto.push([
      "TOTALES",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      `$${reportData.resumen.costo_total.toFixed(2)}`,
      `$${reportData.resumen.total_vendido.toFixed(2)}`,
      `$${reportData.resumen.ganancia_total.toFixed(2)}`,
      `${reportData.resumen.margen_promedio.toFixed(2)}%`
    ]);
    
    const wsPorProducto = XLSX.utils.aoa_to_sheet(dataPorProducto);
    XLSX.utils.book_append_sheet(workbook, wsPorProducto, "Ganancias por Producto");

    // ==========================================
    // HOJA 3: RESUMEN GENERAL
    // ==========================================
    const resumenData = [
      ["REPORTE DE GANANCIAS - RESUMEN"],
      ["Período:", `${reportData.periodo.desde} al ${reportData.periodo.hasta}`],
      [],
      ["RESUMEN GENERAL"],
      ["Total Vendido", `$${reportData.resumen.total_vendido.toFixed(2)}`],
      ["Costo Total", `$${reportData.resumen.costo_total.toFixed(2)}`],
      ["Ganancia Total", `$${reportData.resumen.ganancia_total.toFixed(2)}`],
      ["Margen Promedio", `${reportData.resumen.margen_promedio.toFixed(2)}%`],
      [],
      ["DESGLOSE POR TIPO"],
      ["Productos - Vendido", `$${reportData.desglose.productos.total_vendido.toFixed(2)}`],
      ["Productos - Ganancia", `$${reportData.desglose.productos.ganancia.toFixed(2)}`],
      ["Servicios - Vendido", `$${reportData.desglose.servicios.total_vendido.toFixed(2)}`],
      ["Servicios - Ganancia", `$${reportData.desglose.servicios.ganancia.toFixed(2)}`],
    ];
    const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
    XLSX.utils.book_append_sheet(workbook, wsResumen, "Resumen");

    // Descargar
    const fileName = `Reporte_Ganancias_${fromDate}_al_${toDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    Swal.fire({
      icon: "success",
      title: "✅ Excel Descargado",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Se generaron 3 hojas:</strong></p>
          <ul>
            <li>📅 <strong>Ganancias por Día:</strong> Resumen diario</li>
            <li>📦 <strong>Ganancias por Producto:</strong> Detalle completo</li>
            <li>📊 <strong>Resumen General:</strong> Vista consolidada</li>
          </ul>
        </div>
      `,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#4CAF50"
    });
  };

  // 🔹 Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-SV', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // Datos para el gráfico de pastel
  const getPieData = () => {
    if (!reportData) return [];
    return [
      { 
        name: 'Ganancia Productos', 
        value: reportData.desglose.productos.ganancia,
        color: '#0088FE' 
      },
      { 
        name: 'Ganancia Servicios', 
        value: reportData.desglose.servicios.ganancia,
        color: '#00C49F' 
      }
    ];
  };

  const COLORS = ['#0088FE', '#00C49F'];

  return (
    <div className="profit-report-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">💰 Reporte de Ganancias</h2>
        </div>

        <div className="card-body">
          {/* Filtros */}
          <div className="filters-section">
            <div className="filters-row">
              <div className="form-group">
                <label className="form-label">📅 Fecha Inicio</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">📅 Fecha Fin</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div className="filters-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={handleGenerateReport}
                  disabled={loading}
                >
                  {loading ? "⏳ Generando..." : "🔍 Generar Reporte"}
                </button>
                <button className="btn btn-secondary" onClick={handleClear}>
                  🔄 Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center mt-4">
              <div className="spinner"></div>
              <p className="mt-2">Generando reporte de ganancias...</p>
            </div>
          )}

          {/* Resultados */}
          {reportData && !loading && (
            <div className="report-results">
              {/* Botón Exportar */}
              <div className="export-section">
                <button className="btn btn-success" onClick={handleExportExcel}>
                  📊 Exportar a Excel
                </button>
              </div>

              {/* Resumen General */}
              <div className="summary-section">
                <h3 className="section-title">📈 Resumen General</h3>
                <div className="kpi-cards">
                  <div className="kpi-card kpi-success">
                    <div className="kpi-icon">💵</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Vendido</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.total_vendido)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-danger">
                    <div className="kpi-icon">📉</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Costo Total</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.costo_total)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-primary">
                    <div className="kpi-icon">💰</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Ganancia Total</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.ganancia_total)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-info">
                    <div className="kpi-icon">📊</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Margen Promedio</div>
                      <div className="kpi-value">{reportData.resumen.margen_promedio.toFixed(2)}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desglose Productos vs Servicios */}
              <div className="breakdown-section">
                <h3 className="section-title">📦 Desglose: Productos vs Servicios</h3>
                
                <div className="breakdown-grid">
                  {/* Gráfico de Pastel */}
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={getPieData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {getPieData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Tarjetas de Desglose */}
                  <div className="breakdown-cards">
                    <div className="breakdown-card">
                      <h4>📦 Productos</h4>
                      <div className="breakdown-item">
                        <span>Total Vendido:</span>
                        <strong>{formatCurrency(reportData.desglose.productos.total_vendido)}</strong>
                      </div>
                      <div className="breakdown-item">
                        <span>Ganancia:</span>
                        <strong className="text-success">{formatCurrency(reportData.desglose.productos.ganancia)}</strong>
                      </div>
                    </div>

                    <div className="breakdown-card">
                      <h4>🔧 Servicios</h4>
                      <div className="breakdown-item">
                        <span>Total Vendido:</span>
                        <strong>{formatCurrency(reportData.desglose.servicios.total_vendido)}</strong>
                      </div>
                      <div className="breakdown-item">
                        <span>Ganancia:</span>
                        <strong className="text-success">{formatCurrency(reportData.desglose.servicios.ganancia)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalle por Item */}
              <div className="detail-section">
                <h3 className="section-title">📋 Detalle por Item</h3>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Nombre</th>
                        <th>Presentación</th>
                        <th>Tipo</th>
                        <th>Cant.</th>
                        <th>Unid. Base</th>
                        <th>P. Compra Unit.</th>
                        <th>P. Venta</th>
                        <th>Costo Total</th>
                        <th>Venta Total</th>
                        <th>Ganancia</th>
                        <th>Margen %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.detalle.map((item, index) => (
                        <tr key={index}>
                          <td>{item.codigo}</td>
                          <td>{item.nombre}</td>
                          <td>
                            <span className="badge badge-secondary">
                              {item.presentacion}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${item.tipo === 'Servicio' ? 'badge-info' : 'badge-primary'}`}>
                              {item.tipo}
                            </span>
                          </td>
                          <td className="text-center">{item.cantidad_vendida}</td>
                          <td className="text-center">
                            <span className="text-muted">{item.unidades_base}</span>
                          </td>
                          <td className="text-right">
                            {item.precio_compra_unitario ? formatCurrency(item.precio_compra_unitario) : '-'}
                          </td>
                          <td className="text-right">{formatCurrency(item.precio_venta)}</td>
                          <td className="text-right">{formatCurrency(item.costo_total)}</td>
                          <td className="text-right">{formatCurrency(item.venta_total)}</td>
                          <td className="text-right text-success font-bold">
                            {formatCurrency(item.ganancia_neta)}
                          </td>
                          <td className="text-center">
                            <span className={`badge ${item.margen_porcentaje >= 50 ? 'badge-success' : item.margen_porcentaje >= 20 ? 'badge-warning' : 'badge-danger'}`}>
                              {item.margen_porcentaje.toFixed(2)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Sin datos */}
          {!reportData && !loading && (
            <div className="no-data">
              <p>📭 Selecciona un rango de fechas y genera el reporte de ganancias</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}